export default function StateLawCompliance() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-government-line"></i>
            <span>Legal Compliance</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            State and Local Law Compliance
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Tenant and lease laws vary dramatically by state and city. Understanding your local requirements is essential for legal compliance during property sales.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/c702a7ec05cdd994cb9158258aa8d24d.jpg"
                alt="State and local law compliance for tenant and lease management"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Key Areas of State Law Variation</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              Landlord-tenant laws are primarily governed at the state and local level, with significant variations in tenant protections, notice requirements, and landlord obligations. What's legal in one state may be prohibited in another.
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-notification-3-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Notice Requirements</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      States require different notice periods for property sales, ownership changes, and lease assignments. Ranges from 5 days to 90 days.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Examples:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• California: 60 days for no-fault terminations</li>
                        <li>• New York: 30 days for month-to-month tenancies</li>
                        <li>• Texas: No specific requirement for sale notice</li>
                        <li>• Florida: 30 days for ownership change notification</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-safe-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Security Deposit Regulations</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      States have different rules about deposit amounts, interest requirements, holding accounts, and transfer procedures.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Key Variations:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• Maximum deposit limits (1-3 months rent)</li>
                        <li>• Interest payment requirements</li>
                        <li>• Separate account mandates</li>
                        <li>• Transfer notification timeframes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-user-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Tenant Rights During Sales</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Some jurisdictions provide tenants with right of first refusal, relocation assistance, or protection from sale-related evictions.
                    </p>
                    <div className="bg-amber-50 p-3 rounded-lg">
                      <p className="text-xs text-amber-800">
                        <strong>Example:</strong> Washington DC requires 30-day notice and gives tenants right to purchase before third-party sale
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-percent-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Rent Control and Stabilization</h4>
                    <p className="text-slate-600 text-sm">
                      Cities with rent control have special rules about lease assignments, rent increases, and tenant protections during ownership changes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Property Access and Showings</h4>
                    <p className="text-slate-600 text-sm">
                      States regulate how much notice landlords must provide for showings and how frequently properties can be shown during sales.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Legal Compliance is Critical</h4>
                  <p className="text-red-800 text-sm leading-relaxed">
                    Violations of state or local tenant laws can result in fines, lawsuits, deal cancellations, and even criminal penalties. Always consult with a local real estate attorney before selling tenant-occupied properties.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">State-by-State Compliance Highlights</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">California</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>60-day notice for no-fault evictions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Security deposit interest required</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Just cause eviction laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>24-hour showing notice required</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">New York</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Rent stabilization in NYC</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Interest on deposits (6+ units)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Tenant right of first refusal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Strict eviction procedures</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Texas</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>More landlord-friendly laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>No deposit interest required</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>30-day notice standard</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Faster eviction process</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Florida</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>30-day ownership notice</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Separate account or surety bond</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>No interest on deposits</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>12-hour showing notice</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Illinois</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Chicago has strict ordinances</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Interest on deposits required</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Move-in checklist mandatory</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>2-day showing notice</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Washington</h4>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Strong tenant protections</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>60-day sale notice required</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Relocation assistance in some cities</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>48-hour showing notice</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-sm text-slate-600 mt-6 text-center italic">
            <i className="ri-information-line text-teal-600"></i> This is a general overview. Always consult with a local real estate attorney for specific requirements in your jurisdiction
          </p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Compliance Checklist for Property Sales</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-teal-600 text-xl"></i>
                Before Listing
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Review all state and local landlord-tenant laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Verify security deposit compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Ensure all leases are legally compliant</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Check for rent control or stabilization rules</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Verify proper insurance and licensing</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Consult with local real estate attorney</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-file-shield-line text-teal-600 text-xl"></i>
                During Sale Process
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Provide required notice to tenants</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Follow showing notice requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Maintain required records and documentation</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Comply with tenant right of first refusal (if applicable)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Continue fulfilling all landlord obligations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Document all tenant communications</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-file-transfer-line text-teal-600 text-xl"></i>
                At Closing
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Execute proper lease assignment documents</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Transfer security deposits correctly</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Provide all required tenant files to buyer</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Complete ownership transfer notifications</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Prorate rent and deposits properly</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Comply with final notice requirements</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-mail-send-line text-teal-600 text-xl"></i>
                Post-Closing
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Send final tenant notifications within required timeframe</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Provide forwarding address for tenant correspondence</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Retain copies of all transfer documents</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Confirm buyer has sent required notices</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Maintain records for statute of limitations period</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span>Address any post-closing tenant inquiries promptly</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
